// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';

import { TextField } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

type Props = {
  rootStyle?: {},
  fieldName: string,
};

export function FieldCity({ rootStyle, fieldName }: Props): Node {
  const inputId = useUniqueId();
  const [value, handleChange] = useUserInputAsTextField(fieldName);
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError(fieldName);
  const submitting = useSubmitting();
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('city');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div style={rootStyle} data-test-name="address-city">
      <label htmlFor={inputId}>
        {getFieldLabelByCountry(country)}
      </label>

      <TextField
        id={inputId}
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="address-city"
      />

      {formHasBeenSent && validationError && (
        <ValidationMessageByCountry validationError={validationError} country={country}/>
      )}
    </div>
  );
}


type ValidationMessageProps = {|
  validationError: UserInputValidationError,
  country: string,
|};

function ValidationMessageByCountry({ validationError, country }: ValidationMessageProps): Node {
  switch (country) {
    case 'co':
    case 'pe':
    case 'cl':
    case 'uy':
      return <ValidationMessagesSpanish validationError={validationError} />;

    default:
      throw new Error(`Field City: error messages not mapped for country: ${country}`);
  }
}

type ValidationMessageForLanguageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessagesSpanish({ validationError }: ValidationMessageForLanguageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          El ciudad es requerido
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          El ciudad es inválido
        </ErrorLabel>
      );
  }
}

function getFieldLabelByCountry(country: string): string {
  switch (country) {
    case 'co':
    case 'pe':
    case 'cl':
    case 'cr':
    case 'py':
    case 'pa':
    case 'do':
    case 'sv':
    case 'gt':
      return 'Ciudad';

    case 'uy':
      return 'Ciudad/Barrio';

    case 'za':
      return 'City';

    default:
      throw new Error(`Field City: Label not mapped for country ${country}`);
  }
}
